import {
  CardScheme,
  CardType,
  loadCheckoutWebComponents,
  PaymentSessionResponse,
} from '@checkout.com/checkout-web-components';
import { stringifyAllForDebugging } from '@daymade/common-lib/types';
import clsx from 'clsx';
import { CheckoutComSourceFor3DS } from 'hooks/payment-3ds/useCheckoutCom3DSPayment';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { trackEvent } from 'utils/analytics/analytics';
import {
  CHECKOUT_COM_API_PUBLIC_KEY,
  runningInProduction,
} from 'utils/constants';
import styles from './CheckoutComDropInCheckout.module.css';

interface ICheckoutComDropInCheckout {
  paymentSession: PaymentSessionResponse;
  /** Our internal payment session ID, used to poll for the payment/order status on the /payment/success page. */
  checkoutComPaymentSessionId: string;
  /** Identifies which flow initiated the payment, so /payment/success knows how to check its status. */
  source: CheckoutComSourceFor3DS;
}

const CheckoutComDropInCheckout = observer(
  ({
    paymentSession,
    checkoutComPaymentSessionId,
    source,
  }: ICheckoutComDropInCheckout) => {
    const flowContainer = useRef<HTMLDivElement | null>(null);
    const router = useRouter();

    const { outsideUK } = useUserStore();

    const initialise = async () => {
      const checkout = await loadCheckoutWebComponents({
        publicKey: CHECKOUT_COM_API_PUBLIC_KEY,
        environment: runningInProduction ? 'production' : 'sandbox',
        locale: 'en-GB',
        paymentSession,
        componentOptions: {
          card: {
            displayCardholderName: 'hidden',
          },
          flow: {
            acceptedCardTypes: [CardType.Debit],
            acceptedCardSchemes: [CardScheme.Mastercard, CardScheme.Visa],
          },
        },
        onAuthorized: async (component, authorizeResult: any) => {
          // during sandbox testing, the acceptedCardTypes option above doesn't always disallow credit cards
          // so this is added for extra validation
          if (
            authorizeResult?.payment?.token?.paymentMethod?.type === 'credit' || // Apple Pay
            authorizeResult?.paymentMethodData?.info?.cardFundingSource ===
              'CREDIT' // Google Pay
          ) {
            return {
              continue: false,
              errorMessage: 'Credit cards not supported',
            };
          }

          return { continue: true };
        },
        onPaymentCompleted: (_component, paymentResponse) => {
          console.log('Completed Payment: ', paymentResponse);

          // The payment/order status is polled on the /payment/success page.
          // Checkout.com redirect the browser to the page automatically
          // if 3DS is carried out. In scenarios where 3DS is not carried out
          // (e.g. Paypal), navigate there manually to start polling.
          // If the automatic redirect already fired, this component will have
          // been unmounted and this callback won't run.
          router.push(
            `/payment/success?source=${source}&ckopsid=${checkoutComPaymentSessionId}`,
          );
        },
        onError: (component, error) => {
          console.log(
            'Error caught by CheckoutComDropInCheckout',
            error?.constructor.name,
            stringifyAllForDebugging(error),
          );

          trackEvent('checkout_com_payment_error', {
            id: paymentSession.id,
            reason: error.name,
          });
        },
      });

      if (flowContainer.current) {
        const flowComponent = checkout.create('flow');
        flowComponent.mount(flowContainer.current);
      }
    };

    useEffect(() => {
      (async () => {
        await initialise();
      })();
    }, []);

    if (outsideUK) {
      return (
        <div className={styles.outsideUkContainer}>
          <h3 className="daymadeTypographyMBold">
            It looks like you are visiting us from outside the United Kingdom
          </h3>
          <p className="daymadeTypographyMMedium">
            Please note that DAYMADE operates exclusively in the UK and is
            available to UK residents only. We hope to bring the DAYMADE
            experience to your country one day!
          </p>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div ref={flowContainer} className={styles.flowContainer}></div>

        <p
          className={clsx(
            'daymadeTypographyMRegular',
            'textLightGrey',
            styles.creditCardNote,
          )}
        >
          (Note: Credit Cards not accepted)
        </p>
      </div>
    );
  },
);

export default CheckoutComDropInCheckout;
